import { useState } from 'react'
import {
  Button,
  InputNumber,
  Popconfirm,
  Progress,
  Select,
  Table,
  Tag,
  Typography,
} from 'antd'
import { AimOutlined } from '@ant-design/icons'
import {
  SPACE_TYPE_LABELS,
  NAME_SOURCE_LABELS,
  type SpaceBill,
  type SpaceReviewItem,
  type SpaceType,
} from '@/types/api'

const { Text } = Typography

interface Props {
  spaces: SpaceReviewItem[]
  spaceBill?: SpaceBill | null
  canReview: boolean
  selectedKey: string | null
  onSelect: (spaceKey: string) => void
  updatePending: boolean
  onUpdate: (params: {
    spaceId: string
    version: number
    updates: Partial<Pick<SpaceReviewItem, 'name' | 'space_type' | 'is_deleted'>> & {
      param_overrides?: Record<string, number>
    }
  }) => void
}

type SpaceTableRow = SpaceReviewItem & { children?: SpaceReviewItem[] }

function collectOpenings(bill: SpaceBill | null | undefined): { spaceId: string; count: number; kind: string; isInstall?: boolean }[] {
  if (!bill) return []
  const out: { spaceId: string; count: number; kind: string; isInstall?: boolean }[] = []
  const walk = (rooms: SpaceBill['rooms']) => {
    for (const room of rooms) {
      for (const o of room.openings || []) {
        if (o.space_id || room.space_id) {
          out.push({
            spaceId: o.space_id || room.space_id,
            count: o.count,
            kind: o.kind,
            isInstall: Boolean(o.is_install),
          })
        }
      }
      walk(room.children || [])
    }
  }
  walk(bill.rooms)
  return out
}

function openingText(
  row: SpaceTableRow,
  spaces: SpaceReviewItem[],
  bill: SpaceBill | null | undefined,
): string {
  const keys = new Set<string>([row.space_key])
  if (row.children?.length) {
    for (const c of row.children) keys.add(c.space_key)
  } else {
    for (const s of spaces) {
      if (s.parent_id && s.parent_id === row.id) keys.add(s.space_key)
    }
  }
  let doors = 0
  let windows = 0
  for (const o of collectOpenings(bill)) {
    if (!keys.has(o.spaceId) || o.isInstall) continue
    if (o.kind === 'window') windows += o.count
    else doors += o.count
  }
  if (!doors && !windows) return '—'
  const parts: string[] = []
  if (doors) parts.push(`门 ${doors}樘`)
  if (windows) parts.push(`窗 ${windows}樘`)
  return parts.join(' / ')
}

function nestSpaces(spaces: SpaceReviewItem[]): SpaceTableRow[] {
  const byParent = new Map<string, SpaceReviewItem[]>()
  const tops: SpaceReviewItem[] = []
  for (const s of spaces) {
    if (s.parent_id) {
      const list = byParent.get(s.parent_id) ?? []
      list.push(s)
      byParent.set(s.parent_id, list)
    } else {
      tops.push(s)
    }
  }
  return tops.map((p) => {
    const kids = byParent.get(p.id)
    if (!kids?.length) return p
    return { ...p, children: kids }
  }).concat(
    spaces.filter((s) => {
      if (!s.parent_id) return false
      return !tops.some((p) => p.id === s.parent_id)
    }),
  )
}

export function SpaceReviewTable({
  spaces,
  spaceBill,
  canReview,
  selectedKey,
  onSelect,
  updatePending,
  onUpdate,
}: Props) {
  const dataSource = nestSpaces(spaces)
  const childCount = spaces.filter((s) => s.parent_id).length
  return (
    <Table<SpaceTableRow>
      dataSource={dataSource}
      rowKey="id"
      size="small"
      pagination={{ pageSize: 20 }}
      defaultExpandAllRows
      indentSize={18}
      rowClassName={(r) => (r.space_key === selectedKey ? 'space-row-selected' : '')}
      onRow={(r) => ({
        onClick: () => onSelect(r.space_key),
        style: { cursor: 'pointer' },
      })}
      columns={[
        {
          title: childCount ? `编号（${childCount} 个子空间）` : '编号',
          dataIndex: 'space_key',
          width: 108,
          render: (k: string, r) => (
            <span>
              <Text code>{k}</Text>
              {r.parent_id ? (
                <Tag style={{ marginLeft: 6 }} color="blue">
                  子
                </Tag>
              ) : null}
            </span>
          ),
        },
        {
          title: '名称',
          dataIndex: 'name',
          width: 180,
          render: (name: string, r) =>
            canReview ? (
              <span onClick={(e) => e.stopPropagation()}>
                <InlineNameEditor
                  space={r}
                  onSave={(newName) =>
                    onUpdate({ spaceId: r.id, version: r.version, updates: { name: newName } })
                  }
                />
              </span>
            ) : (
              name
            ),
        },
        {
          title: '类型',
          dataIndex: 'space_type',
          width: 140,
          render: (t: string, r) =>
            canReview ? (
              <span onClick={(e) => e.stopPropagation()}>
                <Select
                  value={t}
                  size="small"
                  style={{ width: 120 }}
                  disabled={updatePending}
                  onChange={(v: SpaceType) =>
                    onUpdate({ spaceId: r.id, version: r.version, updates: { space_type: v } })
                  }
                  options={(Object.keys(SPACE_TYPE_LABELS) as SpaceType[]).map((st) => ({
                    value: st,
                    label: SPACE_TYPE_LABELS[st],
                  }))}
                />
              </span>
            ) : (
              <Tag>{SPACE_TYPE_LABELS[t as SpaceType] || t}</Tag>
            ),
        },
        {
          title: '面积',
          dataIndex: 'area_m2',
          width: 120,
          align: 'right' as const,
          render: (v: number, r) => (
            <span>
              {v.toFixed(2)} m²
              {r.parent_id ? (
                <Text type="secondary" style={{ marginLeft: 4, fontSize: 12 }}>
                  平面图
                </Text>
              ) : null}
            </span>
          ),
        },
        {
          title: '周长',
          dataIndex: 'perimeter_m',
          width: 90,
          align: 'right' as const,
          render: (v: number) => `${v.toFixed(2)} m`,
        },
        {
          title: '门窗',
          width: 120,
          render: (_, r) => (
            <Text type={openingText(r, spaces, spaceBill) === '—' ? 'secondary' : undefined}>
              {openingText(r, spaces, spaceBill)}
            </Text>
          ),
        },
        {
          title: '来源',
          dataIndex: 'name_source',
          width: 88,
          render: (src: string | null | undefined) =>
            src ? (
              <Text type="secondary">{NAME_SOURCE_LABELS[src] || src}</Text>
            ) : (
              <Text type="secondary">-</Text>
            ),
        },
        {
          title: '置信度',
          dataIndex: 'confidence',
          width: 90,
          render: (c: number) => (
            <Progress
              percent={c * 100}
              size="small"
              showInfo={false}
              status={c < 0.5 ? 'exception' : 'normal'}
            />
          ),
        },
        {
          title: '层高覆盖',
          dataIndex: 'param_overrides',
          width: 120,
          render: (p: Record<string, number>, r) =>
            canReview ? (
              <span onClick={(e) => e.stopPropagation()}>
                <InputNumber
                  size="small"
                  min={1.5}
                  max={10}
                  step={0.1}
                  placeholder="项目默认"
                  value={p.floor_height_m}
                  onChange={(v) =>
                    v != null &&
                    onUpdate({
                      spaceId: r.id,
                      version: r.version,
                      updates: { param_overrides: { ...p, floor_height_m: v } },
                    })
                  }
                  style={{ width: 96 }}
                />
              </span>
            ) : p.floor_height_m ? (
              <Text>{p.floor_height_m}m</Text>
            ) : (
              <Text type="secondary">项目默认</Text>
            ),
        },
        {
          title: '状态',
          width: 80,
          render: (_, r) =>
            r.is_deleted ? (
              <Tag color="red">已删</Tag>
            ) : r.is_verified ? (
              <Tag color="green">已确认</Tag>
            ) : (
              <Tag color="orange">待确认</Tag>
            ),
        },
        {
          title: '操作',
          width: 120,
          render: (_, r) => (
            <span onClick={(e) => e.stopPropagation()}>
              <Button
                size="small"
                type="link"
                icon={<AimOutlined />}
                onClick={() => onSelect(r.space_key)}
              >
                定位
              </Button>
              {canReview && !r.is_deleted && (
                <Popconfirm
                  title="删除该空间？"
                  onConfirm={() =>
                    onUpdate({
                      spaceId: r.id,
                      version: r.version,
                      updates: { is_deleted: true },
                    })
                  }
                >
                  <Button size="small" danger type="link">
                    删除
                  </Button>
                </Popconfirm>
              )}
            </span>
          ),
        },
      ]}
    />
  )
}

function InlineNameEditor({
  space,
  onSave,
}: {
  space: SpaceReviewItem
  onSave: (newName: string) => void
}) {
  const [editing, setEditing] = useState(false)
  const [val, setVal] = useState(space.name)

  if (!editing) {
    return (
      <span
        style={{ cursor: 'pointer', borderBottom: '1px dashed #bbb' }}
        onClick={() => setEditing(true)}
        title="点击修改"
      >
        {space.name}
      </span>
    )
  }
  return (
    <input
      autoFocus
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => {
        setEditing(false)
        if (val && val !== space.name) onSave(val)
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
        if (e.key === 'Escape') {
          setVal(space.name)
          setEditing(false)
        }
      }}
      style={{
        width: 140,
        padding: '2px 6px',
        border: '1px solid #40a9ff',
        borderRadius: 4,
        outline: 'none',
      }}
    />
  )
}
